import { rollup, sum, ascending, descending, intersection } from "d3-array";

export function getAllThemesSorted(poemsGroupsByThema) {
  return poemsGroupsByThema
    .top(Infinity)
    .filter(d => d.value.count)
    .sort(
      (a, b) =>
        descending(a.value.count, b.value.count) || ascending(a.key, b.key)
    );
}

export function getAllTargetsOfPickedThemes(
  target,
  pickedThemes,
  filteredPoems
) {
  if (!pickedThemes.length === 0) return [];
  const targetSet = new Set();
  filteredPoems.forEach(d => {
    if (intersection(d.themes, pickedThemes).size > 0) {
      d[`${target}s`].forEach(target => {
        targetSet.add(target);
      });
    }
  });
  return [...targetSet].sort();
}

export function getSankeyData(
  poemsByTheme,
  themesSet,
  pickedPoem,
  moodThreshold,
  motifThreshold,
  pickedMoods,
  pickedMotifs
) {
  // Poems
  const poemsSet = new Set();
  themesSet.forEach(theme => {
    poemsByTheme.get(theme).forEach(poem => {
      const passMoodsFilter =
        pickedMoods.length === 0 ||
        intersection(pickedMoods, poem.moods).size > 0;
      const passMotifsFilter =
        pickedMotifs.length === 0 ||
        intersection(pickedMotifs, poem.motifs).size > 0;
      if (passMoodsFilter && passMotifsFilter) poemsSet.add(poem);
    });
  });
  if (pickedPoem) {
    poemsSet.add(pickedPoem);
  }
  const poems = [...poemsSet];

  // Flows
  let themeToMoodFlows = [
    ...calculateFlows(
      "themes",
      "mood",
      poems,
      themesSet,
      new Set(pickedMoods)
    ).values()
  ];
  if (pickedMoods.length === 0 && moodThreshold > 1) {
    themeToMoodFlows = filterFlowsByThreshold(themeToMoodFlows, moodThreshold);
  }
  let themeToMotifFlows = [
    ...calculateFlows(
      "themes",
      "motif",
      poems,
      themesSet,
      new Set(pickedMotifs)
    ).values()
  ];
  if (pickedMotifs.length === 0 && motifThreshold > 1) {
    themeToMotifFlows = filterFlowsByThreshold(
      themeToMotifFlows,
      motifThreshold
    );
  }

  // Targets set
  const moodsSet = calculateTargetsSet(
    "mood",
    pickedMoods,
    themeToMoodFlows,
    pickedPoem
  );
  const motifsSet = calculateTargetsSet(
    "motif",
    pickedMotifs,
    themeToMotifFlows,
    pickedPoem
  );

  return {
    poems,
    moodsSet,
    themeToMoodFlows,
    motifsSet,
    themeToMotifFlows
  };
}

function calculateFlows(source, target, poems, sourcesSet, targetSet) {
  const flows = new Map();
  poems.forEach(poem => {
    (Array.isArray(poem[source]) ? poem[source] : [poem[source]]).forEach(
      source => {
        if (sourcesSet.has(source)) {
          poem[`${target}s`].forEach(target => {
            if (targetSet.size === 0 || targetSet.has(target)) {
              const id = `${source}-${target}`;
              if (flows.has(id)) {
                const flow = flows.get(id);
                flow.poems.add(poem);
              } else {
                flows.set(id, {
                  source,
                  target,
                  poems: new Set([poem])
                });
              }
            }
          });
        }
      }
    );
  });
  flows.forEach(flow => {
    flow.value = flow.poems.size;
    Object.freeze(flow);
  });
  return flows;
}

function filterFlowsByThreshold(flows, threshold) {
  let countByTarget;
  countByTarget = rollup(
    flows,
    v => sum(v, d => d.value),
    d => d.target
  );
  return flows.filter(f => countByTarget.get(f.target) >= threshold);
}

function calculateTargetsSet(target, pickedTargets, flows, pickedPoem) {
  let targetsSet;
  if (pickedTargets.length === 0) {
    // Derive targets from the flows when there's no picked targets
    targetsSet = new Set();
    flows.forEach(f => targetsSet.add(f.target));
  } else {
    // Only use picked targets
    targetsSet = new Set(pickedTargets);
  }

  if (pickedPoem) {
    for (const t of pickedPoem[`${target}s`]) {
      targetsSet.add(t);
    }
  }
  return targetsSet;
}

// Sankey selection tracking
export function toggleArrayItem(array, item) {
  const i = array.indexOf(item);
  let newArray = array.slice();
  if (i === -1) {
    newArray.push(item);
  } else {
    newArray.splice(i, 1);
  }
  return newArray;
}

export function getHighlightedFromThemes(
  allToMoodFlows,
  allToMotifFlows,
  themes
) {
  const moodSet = new Set();
  const motifSet = new Set();
  const toMoodFlowSet = new Set();
  const toMotifFlowSet = new Set();
  const poemSet = new Set();
  allToMoodFlows.forEach(f => {
    if (themes.includes(f.source)) {
      moodSet.add(f.target);
      toMoodFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  allToMotifFlows.forEach(f => {
    if (themes.includes(f.source)) {
      motifSet.add(f.target);
      toMotifFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  return {
    moods: [...moodSet],
    motifs: [...motifSet],
    toMoodFlows: [...toMoodFlowSet],
    toMotifFlows: [...toMotifFlowSet],
    poems: [...poemSet]
  };
}

export function getHighlightedFromMoods(
  allToMoodFlows,
  allToMotifFlows,
  moods
) {
  const themeSet = new Set();
  const motifSet = new Set();
  const toMoodFlowSet = new Set();
  const toMotifFlowSet = new Set();
  const poemSet = new Set();
  allToMoodFlows.forEach(f => {
    if (moods.includes(f.target)) {
      themeSet.add(f.source);
      toMoodFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  allToMotifFlows.forEach(f => {
    if (themeSet.has(f.source) && [...f.poems].some(p => poemSet.has(p))) {
      motifSet.add(f.target);
      toMotifFlowSet.add(f);
    }
  });
  return {
    themes: [...themeSet],
    motifs: [...motifSet],
    toMoodFlows: [...toMoodFlowSet],
    toMotifFlows: [...toMotifFlowSet],
    poems: [...poemSet]
  };
}

export function getHighlightedFromMotifs(
  allToMoodFlows,
  allToMotifFlows,
  motifs
) {
  const themeSet = new Set();
  const moodSet = new Set();
  const toMoodFlowSet = new Set();
  const toMotifFlowSet = new Set();
  const poemSet = new Set();
  allToMotifFlows.forEach(f => {
    if (motifs.includes(f.target)) {
      themeSet.add(f.source);
      toMotifFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  allToMoodFlows.forEach(f => {
    if (themeSet.has(f.source) && [...f.poems].some(p => poemSet.has(p))) {
      moodSet.add(f.target);
      toMoodFlowSet.add(f);
    }
  });
  return {
    themes: [...themeSet],
    moods: [...moodSet],
    toMoodFlows: [...toMoodFlowSet],
    toMotifFlows: [...toMotifFlowSet],
    poems: [...poemSet]
  };
}

export function getHighlightedFromThemeMoodFlows(allToMotifFlows, toMoodFlows) {
  const themeSet = new Set();
  const moodSet = new Set();
  const motifSet = new Set();
  const toMotifFlowSet = new Set();
  const poemSet = new Set();
  toMoodFlows.forEach(f => {
    themeSet.add(f.source);
    moodSet.add(f.target);
    f.poems.forEach(p => poemSet.add(p));
  });
  allToMotifFlows.forEach(f => {
    if (themeSet.has(f.source) && [...f.poems].some(p => poemSet.has(p))) {
      motifSet.add(f.target);
      toMotifFlowSet.add(f);
    }
  });
  return {
    themes: [...themeSet],
    moods: [...moodSet],
    motifs: [...motifSet],
    toMotifFlows: [...toMotifFlowSet],
    poems: [...poemSet]
  };
}

export function getHighlightedFromThemeMotifFlows(
  allToMoodFlows,
  toMotifFlows
) {
  const themeSet = new Set();
  const moodSet = new Set();
  const motifSet = new Set();
  const toMoodFlowSet = new Set();
  const poemSet = new Set();
  toMotifFlows.forEach(f => {
    themeSet.add(f.source);
    motifSet.add(f.target);
    f.poems.forEach(p => poemSet.add(p));
  });
  allToMoodFlows.forEach(f => {
    if (themeSet.has(f.source) && [...f.poems].some(p => poemSet.has(p))) {
      moodSet.add(f.target);
      toMoodFlowSet.add(f);
    }
  });
  return {
    themes: [...themeSet],
    moods: [...moodSet],
    motifs: [...motifSet],
    toMoodFlows: [...toMoodFlowSet],
    poems: [...poemSet]
  };
}

export function getHighlightedFromPoem(allToMoodFlows, allToMotifFlows, poem) {
  const themeSet = new Set();
  const moodSet = new Set();
  const motifSet = new Set();
  const toMoodFlowSet = new Set();
  const toMotifFlowSet = new Set();
  const poemSet = new Set();
  allToMoodFlows.forEach(f => {
    if (f.poems.has(poem)) {
      themeSet.add(f.source);
      moodSet.add(f.target);
      toMoodFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  allToMotifFlows.forEach(f => {
    if (f.poems.has(poem)) {
      themeSet.add(f.source);
      motifSet.add(f.target);
      toMotifFlowSet.add(f);
      f.poems.forEach(p => poemSet.add(p));
    }
  });
  return {
    themes: [...themeSet],
    moods: [...moodSet],
    motifs: [...motifSet],
    toMoodFlows: [...toMoodFlowSet],
    toMotifFlows: [...toMotifFlowSet],
    poems: [...poemSet]
  };
}

export function getPoemsFromTheme(
  theme,
  topThemesToMotifsFlows,
  topThemesToMoodsFlows
) {
  return [
    ...new Set(
      []
        .concat(
          ...topThemesToMotifsFlows
            .filter(f => f.source === theme)
            .map(f => [...f.poems])
        )
        .concat(
          ...topThemesToMoodsFlows
            .filter(f => f.source === theme)
            .map(f => [...f.poems])
        )
    )
  ];
}

export function getWordsFromPoem(
  poem,
  topThemesSet,
  topMotifsSet,
  topMoodsSet
) {
  const themes = poem.themes.filter(theme => topThemesSet.has(theme));
  const moods = poem.moods.filter(mood => topMoodsSet.has(mood));
  const motifs = poem.motifs.filter(motif => topMotifsSet.has(motif));
  return {
    themes,
    moods,
    motifs
  };
}
